import { RefObject, useEffect } from 'react';
import { uiAnchorRegistry, type UiAnchorMetadata } from '../services/uiAnchorRegistry';

export function useRegisterUiAnchor<T extends Element>(
  anchorId: string,
  ref: RefObject<T | null>,
  meta?: UiAnchorMetadata,
  deps: ReadonlyArray<unknown> = []
) {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const element = ref.current;
    if (!element) {
      uiAnchorRegistry.unregister(anchorId);
      return undefined;
    }

    let frameId: number | null = null;

    const commit = () => {
      if (ref.current) {
        uiAnchorRegistry.updateFromElement(anchorId, ref.current, meta);
      }
    };

    const scheduleCommit = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        commit();
      });
    };

    commit();

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleCommit) : null;
    resizeObserver?.observe(element);

    window.addEventListener('resize', scheduleCommit);
    window.addEventListener('scroll', scheduleCommit, true);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleCommit);
      window.removeEventListener('scroll', scheduleCommit, true);
      uiAnchorRegistry.unregister(anchorId);
    };
  }, [anchorId, meta?.kind, meta?.ownerId, ref, ...deps]);
}
